import { useQueries, useQuery } from "@tanstack/react-query";
import { cn } from "cn";
import {
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  FileText,
  MonitorPlay,
  Paperclip,
  Upload,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../../api";
import type { IssueAttachment, IssueDetail } from "../../types";
import { SectionHeader } from "../page";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Spinner } from "../ui/spinner";

const MAX_ATTACHMENT_BYTES = 200 * 1024 * 1024;

type PreviewEntry = {
  id: string;
  fileName: string;
  sizeBytes: number;
  kind: PreviewKind;
  /** Set when the file is still local and has not been uploaded. */
  src?: string;
};

/** A file chosen on the new-issue form, uploaded after the issue exists. */
export type StagedAttachment = {
  id: string;
  file: File;
  previewUrl: string | null;
};

export async function uploadIssueFile(ref: string, file: File): Promise<IssueAttachment> {
  const contentType = file.type || "application/octet-stream";
  if (file.size > MAX_ATTACHMENT_BYTES) throw new Error(`${file.name} exceeds the 200 MB limit`);
  const { attachment, uploadUrl } = await api.createAttachment(ref, {
    fileName: file.name,
    contentType,
    sizeBytes: file.size,
  });
  const put = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "content-type": contentType },
    body: file,
  });
  if (!put.ok) throw new Error(`Upload failed (HTTP ${put.status})`);
  const completed = await api.completeAttachment(attachment.id);
  return completed.attachment;
}

export function AttachmentsBlock({
  issue,
  onChanged,
  readOnly = false,
}: {
  issue: IssueDetail;
  onChanged: () => void;
  readOnly?: boolean;
}) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Test step evidence shows on its step, in the testing dialog.
  const attachments = issue.attachments.filter((attachment) => !attachment.testStepId);
  const previewable = attachments.flatMap((attachment) => {
    const kind = previewKind(attachment);
    return kind
      ? [
          {
            id: attachment.id,
            fileName: attachment.fileName,
            sizeBytes: attachment.sizeBytes,
            kind,
          },
        ]
      : [];
  });

  const upload = async (files: FileList | File[]) => {
    for (const file of Array.from(files)) {
      setUploading(file.name);
      try {
        await uploadIssueFile(issue.identifier, file);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Upload failed");
      }
    }
    setUploading(null);
    onChanged();
  };

  if (readOnly && attachments.length === 0) return null;

  return (
    <section
      aria-label="Attachments"
      className={cn(
        "-m-1.5 rounded-lg p-1.5 transition-shadow",
        dragging && "bg-primary/5 ring-2 ring-primary/40",
      )}
      onDragOver={(e) => {
        if (readOnly) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        if (readOnly) return;
        e.preventDefault();
        setDragging(false);
        if (e.dataTransfer.files.length) void upload(e.dataTransfer.files);
      }}
    >
      <SectionHeader
        title="Attachments"
        count={attachments.length || undefined}
        description={uploading ? `Uploading ${uploading}…` : undefined}
        className="mb-1.5"
        actions={
          !readOnly && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-xs text-muted-foreground"
                onClick={() => inputRef.current?.click()}
              >
                <Upload className="size-3.5" /> Upload
              </Button>
              <input
                ref={inputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => e.target.files && void upload(e.target.files)}
              />
            </>
          )
        }
      />
      {attachments.length === 0 && !uploading ? (
        <button
          type="button"
          disabled={readOnly}
          onClick={() => inputRef.current?.click()}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-4 text-xs text-muted-foreground transition-colors duration-(--motion-dur) hover:border-foreground/30 hover:text-foreground"
        >
          <Paperclip className="size-3.5" />
          Drop files or click to attach. Screenshots pasted into comments land here too.
        </button>
      ) : (
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-2">
          {attachments.map((attachment) => (
            <AttachmentCard
              key={attachment.id}
              attachment={attachment}
              onPreview={previewKind(attachment) ? () => setPreviewId(attachment.id) : undefined}
            />
          ))}
        </ul>
      )}
      <AttachmentPreviewDialog
        items={previewable}
        activeId={previewId}
        onActiveIdChange={setPreviewId}
        onOpenChange={(open) => {
          if (!open) setPreviewId(null);
        }}
      />
    </section>
  );
}

type PreviewKind = "image" | "video" | "pdf";

function previewKindOf(contentType: string, fileName: string): PreviewKind | null {
  const type = contentType.toLowerCase();
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("video/")) return "video";
  if (type === "application/pdf" || fileName.toLowerCase().endsWith(".pdf")) return "pdf";
  return null;
}

function previewKind(attachment: IssueAttachment): PreviewKind | null {
  return previewKindOf(attachment.contentType, attachment.fileName);
}

/** A file tile: its preview or type icon above the name, type and size. */
function AttachmentCard({ attachment, onPreview }: { attachment: IssueAttachment; onPreview?: () => void }) {
  const kind = previewKind(attachment);
  const url = useQuery({
    queryKey: ["attachment-url", attachment.id],
    queryFn: () => api.attachmentUrl(attachment.id),
    staleTime: 10 * 60_000,
    enabled: kind !== null,
  });
  const open = async () => {
    if (onPreview) {
      onPreview();
      return;
    }
    const { url: href } = await api.attachmentUrl(attachment.id);
    window.open(href, "_blank", "noreferrer");
  };

  let preview: ReactNode;
  if (kind === "image" && url.data) {
    preview = <img src={url.data.url} alt="" className="size-full object-cover" />;
  } else if (kind === "video" && url.data) {
    preview = (
      <>
        <video src={url.data.url} muted preload="metadata" className="size-full object-cover" />
        <MonitorPlay className="absolute inset-0 m-auto size-6 text-white drop-shadow" />
      </>
    );
  } else {
    preview = <FileText className="size-6 text-muted-foreground/70" />;
  }

  return (
    <li className="min-w-0">
      <button
        type="button"
        onClick={() => void open()}
        aria-label={attachment.fileName}
        className="group/file block w-full overflow-hidden rounded-lg border bg-card text-left transition-[border-color,box-shadow] duration-(--motion-dur) hover:border-foreground/20 hover:shadow-xs"
      >
        <span className="relative flex aspect-video items-center justify-center overflow-hidden bg-muted/50">
          {preview}
        </span>
        <span className="block border-t px-2.5 py-1.5">
          <span className="block truncate text-xs font-medium">{attachment.fileName}</span>
          <span className="block truncate text-[11px] text-muted-foreground">
            {fileTypeLabel(attachment)} · {formatBytes(attachment.sizeBytes)}
          </span>
        </span>
      </button>
    </li>
  );
}

function fileKindLabel(contentType: string, fileName: string): string {
  const kind = previewKindOf(contentType, fileName);
  if (kind === "image") return "Image";
  if (kind === "video") return "Video";
  if (kind === "pdf") return "PDF";
  const extension = fileName.split(".").pop();
  return extension && extension !== fileName ? extension.toUpperCase() : "File";
}

function fileTypeLabel(attachment: IssueAttachment): string {
  return fileKindLabel(attachment.contentType, attachment.fileName);
}

function AttachmentPreviewDialog({
  items,
  activeId,
  onActiveIdChange,
  onOpenChange,
}: {
  items: PreviewEntry[];
  activeId: string | null;
  onActiveIdChange: (id: string) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const index = items.findIndex((item) => item.id === activeId);
  const open = index >= 0;
  const lastIndex = useRef(0);
  if (index >= 0) lastIndex.current = index;
  const displayIndex = Math.min(lastIndex.current, Math.max(items.length - 1, 0));
  const current = items[displayIndex];
  const multiple = items.length > 1;
  const thumbRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const urls = useQueries({
    queries: items.map((item) => ({
      queryKey: ["attachment-url", item.id],
      queryFn: () => api.attachmentUrl(item.id),
      staleTime: 10 * 60_000,
      enabled: open && !item.src,
    })),
  });
  const srcAt = (itemIndex: number) => items[itemIndex]?.src ?? urls[itemIndex]?.data?.url;
  const url = srcAt(displayIndex);

  const step = (delta: number) => {
    if (!multiple || index < 0) return;
    const next = (index + delta + items.length) % items.length;
    const item = items[next];
    if (item) onActiveIdChange(item.id);
  };

  useEffect(() => {
    if (!open) return;
    thumbRefs.current[displayIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [open, displayIndex]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        sheet={false}
        className="top-4 bottom-4 flex h-auto w-[min(72rem,calc(100%-1rem))] max-w-[min(72rem,calc(100%-1rem))] translate-y-0 flex-col overflow-hidden sm:top-8 sm:bottom-8 sm:w-[min(72rem,calc(100%-4rem))] sm:max-w-[min(72rem,calc(100%-4rem))]"
        onKeyDown={(event) => {
          if (!multiple) return;
          if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
          event.preventDefault();
          step(event.key === "ArrowLeft" ? -1 : 1);
        }}
      >
        <DialogHeader className="shrink-0 pr-8">
          <DialogTitle className="truncate">{current?.fileName}</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {current && formatBytes(current.sizeBytes)}
            {multiple && (
              <span>
                {displayIndex + 1} of {items.length}
              </span>
            )}
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 hover:text-foreground"
              >
                Open in new tab <ArrowUpRight className="size-3.5" />
              </a>
            )}
          </DialogDescription>
        </DialogHeader>
        <div
          className={cn(
            "relative flex min-h-0 flex-1 items-center justify-center",
            multiple && "px-12 sm:px-16",
          )}
        >
          {multiple && (
            <>
              <Button
                type="button"
                variant="outline"
                size="icon-lg"
                className="absolute top-1/2 left-3 z-10 -translate-y-1/2 bg-background/80"
                aria-label="Previous attachment"
                onClick={() => step(-1)}
              >
                <ChevronLeft />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon-lg"
                className="absolute top-1/2 right-3 z-10 -translate-y-1/2 bg-background/80"
                aria-label="Next attachment"
                onClick={() => step(1)}
              >
                <ChevronRight />
              </Button>
            </>
          )}
          {!current || !url ? (
            <Spinner />
          ) : current.kind === "image" ? (
            <img
              key={current.id}
              src={url}
              alt={current.fileName}
              className="max-h-full max-w-full rounded-md object-contain"
            />
          ) : current.kind === "video" ? (
            // biome-ignore lint/a11y/useMediaCaption: user uploads have no captions
            <video
              key={current.id}
              src={url}
              controls
              autoPlay
              className="max-h-full max-w-full rounded-md"
            />
          ) : (
            <iframe
              key={current.id}
              src={url}
              title={current.fileName}
              className="h-full w-full rounded-md border"
            />
          )}
        </div>
        {multiple && (
          <div className="shrink-0 overflow-x-auto pt-3 sm:px-12">
            <div className="mx-auto flex w-max gap-2">
              {items.map((item, itemIndex) => {
                const thumbUrl = srcAt(itemIndex);
                const selected = item.id === activeId;
                return (
                  <button
                    key={item.id}
                    ref={(node) => {
                      thumbRefs.current[itemIndex] = node;
                    }}
                    type="button"
                    aria-label={item.fileName}
                    aria-current={selected ? "true" : undefined}
                    onClick={() => onActiveIdChange(item.id)}
                    className={cn(
                      "block size-14 shrink-0 overflow-hidden rounded-md border bg-muted",
                      selected ? "ring-2 ring-primary" : "opacity-70 hover:opacity-100",
                    )}
                  >
                    <AttachmentThumbnail item={item} url={thumbUrl} />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AttachmentThumbnail({ item, url }: { item: PreviewEntry; url: string | undefined }) {
  if (item.kind === "image" && url) {
    return <img src={url} alt="" className="size-full object-cover" />;
  }
  if (item.kind === "video" && url) {
    return (
      <span className="relative block size-full">
        <video src={url} muted preload="metadata" className="size-full object-cover" />
        <MonitorPlay className="absolute inset-0 m-auto size-4 text-white drop-shadow" />
      </span>
    );
  }
  return (
    <span className="flex size-full items-center justify-center">
      <FileText className="size-4 text-muted-foreground" />
    </span>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function clipboardFiles(data: DataTransfer | null): File[] {
  if (!data) return [];
  const listed = Array.from(data.files);
  if (listed.length) return listed;
  return Array.from(data.items).flatMap((item) => {
    if (item.kind !== "file") return [];
    const file = item.getAsFile();
    return file ? [file] : [];
  });
}

/**
 * Files for an issue that does not exist yet. They stay in the browser until
 * create, then upload through the same path as attachments on an issue.
 */
export function DraftAttachments({
  files,
  onChange,
  uploading = null,
  disabled = false,
}: {
  files: StagedAttachment[];
  onChange: (files: StagedAttachment[]) => void;
  uploading?: string | null;
  disabled?: boolean;
}) {
  const [dragging, setDragging] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const filesRef = useRef(files);
  filesRef.current = files;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  const add = (list: FileList | File[]) => {
    if (disabledRef.current) return;
    const next = [...filesRef.current];
    for (const file of Array.from(list)) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`${file.name} exceeds the 200 MB limit`);
        continue;
      }
      const kind = previewKindOf(file.type, file.name);
      next.push({
        id: crypto.randomUUID(),
        file,
        previewUrl: kind ? URL.createObjectURL(file) : null,
      });
    }
    if (next.length !== filesRef.current.length) onChangeRef.current(next);
  };
  const addRef = useRef(add);
  addRef.current = add;

  useEffect(() => {
    return () => {
      for (const item of filesRef.current) {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      }
    };
  }, []);

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const pasted = clipboardFiles(event.clipboardData);
      if (!pasted.length || disabledRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      addRef.current(pasted);
    };
    const hasFiles = (event: DragEvent) => event.dataTransfer?.types.includes("Files") ?? false;
    const onDragOver = (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      if (!disabledRef.current) setDragging(true);
    };
    const onDrop = (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      setDragging(false);
      if (event.dataTransfer?.files.length) addRef.current(event.dataTransfer.files);
    };
    const onDragEnd = () => setDragging(false);
    window.addEventListener("paste", onPaste, true);
    window.addEventListener("dragover", onDragOver, true);
    window.addEventListener("drop", onDrop, true);
    window.addEventListener("dragend", onDragEnd);
    return () => {
      window.removeEventListener("paste", onPaste, true);
      window.removeEventListener("dragover", onDragOver, true);
      window.removeEventListener("drop", onDrop, true);
      window.removeEventListener("dragend", onDragEnd);
    };
  }, []);

  const remove = (id: string) => {
    const removed = files.find((item) => item.id === id);
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    onChange(files.filter((item) => item.id !== id));
  };

  const previewable: PreviewEntry[] = files.flatMap((item) => {
    const kind = previewKindOf(item.file.type, item.file.name);
    return kind && item.previewUrl
      ? [{ id: item.id, fileName: item.file.name, sizeBytes: item.file.size, kind, src: item.previewUrl }]
      : [];
  });

  return (
    <section
      aria-label="Attachments"
      className={cn(
        "-m-1.5 rounded-lg p-1.5 transition-shadow",
        dragging && "bg-primary/5 ring-2 ring-primary/40",
      )}
    >
      <SectionHeader
        title="Attachments"
        count={files.length || undefined}
        description={uploading ? `Uploading ${uploading}…` : undefined}
        className="mb-1.5"
        actions={
          !disabled && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-xs text-muted-foreground"
                onClick={() => inputRef.current?.click()}
              >
                <Upload className="size-3.5" /> Upload
              </Button>
              <input
                ref={inputRef}
                type="file"
                multiple
                hidden
                onChange={(event) => {
                  if (event.target.files) add(event.target.files);
                  event.target.value = "";
                }}
              />
            </>
          )
        }
      />
      {files.length === 0 && !uploading ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-4 text-xs text-muted-foreground transition-colors duration-(--motion-dur) hover:border-foreground/30 hover:text-foreground"
        >
          <Paperclip className="size-3.5" />
          Drop files, paste a screenshot, or click to attach.
        </button>
      ) : (
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-2">
          {files.map((item) => (
            <StagedFileCard
              key={item.id}
              item={item}
              disabled={disabled}
              onOpen={() => {
                if (item.previewUrl && previewKindOf(item.file.type, item.file.name)) {
                  setPreviewId(item.id);
                  return;
                }
                const url = URL.createObjectURL(item.file);
                window.open(url, "_blank", "noreferrer");
                window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
              }}
              onRemove={() => remove(item.id)}
            />
          ))}
        </ul>
      )}
      <AttachmentPreviewDialog
        items={previewable}
        activeId={previewId}
        onActiveIdChange={setPreviewId}
        onOpenChange={(open) => {
          if (!open) setPreviewId(null);
        }}
      />
    </section>
  );
}

function StagedFileCard({
  item,
  disabled,
  onOpen,
  onRemove,
}: {
  item: StagedAttachment;
  disabled: boolean;
  onOpen: () => void;
  onRemove: () => void;
}) {
  const kind = previewKindOf(item.file.type, item.file.name);
  const src = item.previewUrl;
  let preview: ReactNode;
  if (kind === "image" && src) {
    preview = <img src={src} alt="" className="size-full object-cover" />;
  } else if (kind === "video" && src) {
    preview = (
      <>
        <video src={src} muted preload="metadata" className="size-full object-cover" />
        <MonitorPlay className="absolute inset-0 m-auto size-6 text-white drop-shadow" />
      </>
    );
  } else {
    preview = <FileText className="size-6 text-muted-foreground/70" />;
  }

  return (
    <li className="relative min-w-0">
      <button
        type="button"
        onClick={onOpen}
        aria-label={item.file.name}
        className="group/file block w-full overflow-hidden rounded-lg border bg-card text-left transition-[border-color,box-shadow] duration-(--motion-dur) hover:border-foreground/20 hover:shadow-xs"
      >
        <span className="relative flex aspect-video items-center justify-center overflow-hidden bg-muted/50">
          {preview}
        </span>
        <span className="block border-t px-2.5 py-1.5">
          <span className="block truncate text-xs font-medium">{item.file.name}</span>
          <span className="block truncate text-[11px] text-muted-foreground">
            {fileKindLabel(item.file.type, item.file.name)} · {formatBytes(item.file.size)}
          </span>
        </span>
      </button>
      {!disabled && (
        <Button
          type="button"
          variant="secondary"
          size="icon-xs"
          aria-label={`Remove ${item.file.name}`}
          className="absolute top-1.5 right-1.5"
          onClick={onRemove}
        >
          <X />
        </Button>
      )}
    </li>
  );
}
